import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setQtyOrdered from "@insite/client-framework/Store/Pages/RfqJobQuoteDetails/Handlers/SetQtyOrdered";
import translate from "@insite/client-framework/Translate";
import { JobQuoteLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@ucs/MobiusOverrides/TextField/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { validateQuantityField } from "@ucs/Helpers/FormFieldsValidation";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    jobQuoteLine: JobQuoteLineModel;
    isEditable: boolean;
    showLineNotes: boolean;
    currencySymbol: string;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    qtyOrdered: state.pages.rfqJobQuoteDetails.qtyOrderedByJobQuoteLineId[ownProps.jobQuoteLine.id] ?? 0,
});

const mapDispatchToProps = {
    setQtyOrdered,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqJobQuoteDetailsProductCardStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    quoteLineInfoGridItem?: GridItemProps;
    quoteLineInfoContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    productPrice?: ProductPriceStyles;
    unitPriceAndQuantitiesGridItem?: GridItemProps;
    unitPriceAndQuantitiesContainer?: GridContainerProps;
    jobQuantityGridItem?: GridItemProps;
    jobQuantityHeadingAndText?: SmallHeadingAndTextStyles;
    purchasedQuantityGridItem?: GridItemProps;
    purchasedQuantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityRemainingGridItem?: GridItemProps;
    quantityRemainingHeadingAndText?: SmallHeadingAndTextStyles;
    qtyOrderedGridItem?: GridItemProps;
    qtyOrderedTextField?: TextFieldPresentationProps;
    notesGridItem?: GridItemProps;
    toggleNotesLink?: LinkPresentationProps;
    notesText?: TypographyPresentationProps;
    unitPriceGridItem?:GridItemProps;
    PriceLabel?: TypographyPresentationProps;
}

export const rfqJobQuoteDetailsProductCardStyles: RfqJobQuoteDetailsProductCardStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: [3, 3, 2, 2, 2],
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-top: 7px !important;
                    `,
                    css`
                        margin-top: 7px !important;;
                    `,
                ]);
            }}
        `,
    },
    quoteLineInfoGridItem: {
        width: [9, 9, 10, 10, 10],
    },
    quoteLineInfoContainer: {
        gap: 20,
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
            css: css`
            padding-top: 0px;
        `,
    },
    infoContainer: {
        gap: 20,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: fit-content;
                box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.2);
            `,
        },
    },
    productBrand: {
        nameLink: {
            typographyProps: {
                transform: "uppercase",
                css: css`
                    font-size: 13px;
                    color: ${UCSColors.gray04} !important;
                    line-height: 16px;
                    margin-bottom: 7px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                font-size: 12px;
                                margin-bottom: 2px;
                            `,
                            css`
                                font-size: 12px;
                                margin-bottom: 2px;
                            `,
                        ]);
                    }}
                `,
            },
        },
    },
    productPrice: {
        price: {
            priceText: { 
                size: 18,
                weight: 400,
                color: UCSColors.gray03,
                css: css`
                    line-height: 24px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    font-size: 17px;
                                `,
                            ],
                            "max",
                        );
                    }}
                `,
            },
            unitOfMeasureText: { 
                size: 18,
                weight: 400,
                color: UCSColors.gray03,
                css: css`
                    line-height: 24px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    font-size: 17px;
                                `,
                            ],
                            "max",
                        );
                    }}
                `,
             },
        },
        savingsMessageText: { size: 14 },
    },
    unitPriceAndQuantitiesGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
            padding-top: 0px;
        `,
    },
    unitPriceAndQuantitiesContainer: {
        gap: 20,
    },
    jobQuantityGridItem: {
        width: [6, 6, 6, 6, 6],
        css:css`
    `,
},
    unitPriceGridItem: {
        width: [12, 12, 12, 12, 12],
        css:css`
            display: block;
            padding-top: 0px;
        `,
    },
    purchasedQuantityGridItem: {
        width: [6, 6, 6, 6, 6],
    },
    quantityRemainingGridItem: {
        width: [6, 6, 6, 6, 6],
    },
    qtyOrderedGridItem: {
        width: [5, 5, 4, 4, 4],
    },
    notesGridItem: {
        width: 12,
    },
    toggleNotesLink: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    PriceLabel:{
        size: 13,
        weight: 700,
        color: UCSColors.gray04,
        transform: "uppercase",
        css: css`
            line-height: 16px;
            letter-spacing: 0.1em;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 12px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    jobQuantityHeadingAndText: {
        heading: {
            size: 13,
            weight: 700,
            color: UCSColors.gray04,
            transform: "uppercase",
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: 12px;
                            `,
                        ],
                        "max",
                    );
                }}
            `,
        },
        text: {
            size: 18,
            weight: 400,
            color: UCSColors.gray03,
            css: css`
                line-height: 24px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: 17px;
                            `,
                        ],
                        "max",
                    );
                }}
            `,
        },
    },
    purchasedQuantityHeadingAndText: {
        heading: {
            size: 13,
            weight: 700,
            color: UCSColors.gray04,
            transform: "uppercase",
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: 12px;
                            `,
                        ],
                        "max",
                    );
                }}
            `,
        },
        text: {
            size: 18,
            weight: 400,
            color: UCSColors.gray03,
            css: css`
                line-height: 24px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: 17px;
                            `,
                        ],
                        "max",
                    );
                }}
            `,
        },
    },
    quantityRemainingHeadingAndText: {
        heading: {
            size: 13,
            weight: 700,
            color: UCSColors.gray04,
            transform: "uppercase",
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: 12px;
                            `,
                        ],
                        "max",
                    );
                }}
            `,
        },
        text: {
            size: 18,
            weight: 400,
            color: UCSColors.gray03,
            css: css`
                line-height: 24px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: 17px;
                            `,
                        ],
                        "max",
                    );
                }}
            `,
        },
    },
    qtyOrderedTextField: {
        labelProps:{
            size: 13,
            weight: 700,
            color: UCSColors.gray04,
            transform: "uppercase",
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
                min-height: auto;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: 12px;
                            `,
                        ],
                        "max",
                    );
                }}
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width:70%;
                        `,
                        css`
                            width:70%;
                        `,
                        css`
                            width:70%;
                    `,
                        css`
                            width:70%;
                    `,
                    ],
                    "max",
                );
            }}
    
            `,
        },
    },
    productPartNumbers: {
        erpNumberLabelText: {
            weight:400,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                size: 18,
                color: UCSColors.gray00,
                weight: 500,
                lineHeight: "20px",
                css: css`
                    margin-bottom: 8px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                font-size: 16px;
                                line-height: 18px;
                            `,
                            css`
                                font-size: 16px;
                                line-height: 18px;
                            `,
                        ]);
                    }}
                `,
            },
        },
    },
};

const styles = rfqJobQuoteDetailsProductCardStyles;

const RfqJobQuoteDetailsProductCard = ({
    jobQuoteLine,
    isEditable,
    showLineNotes,
    currencySymbol,
    qtyOrdered,
    setQtyOrdered,
}: Props) => {
    const qtyOrderedSubmitHandler = (value: string) => {
        const inputQty = Number.parseInt(value, 10);

        setQtyOrdered({
            jobQuoteLineId: jobQuoteLine.id,
            qtyOrdered: Number.isNaN(inputQty) ? 0 : inputQty,
        });
    };

    const {
        value: localOrderQuantity,
        changeHandler: qtyOrderedChangeHandler,
        keyDownHandler: qtyOrderedKeyDownHandler,
        blurHandler: qtyOrderedBlurHandler,
    } = useAccessibleSubmit(qtyOrdered.toString(), qtyOrderedSubmitHandler);

    const [showNotes, setShowNotes] = useState(false);
    const [quantityError, setQuantityError] = useState("");

    const toggleNotesClickHandler = () => {
        setShowNotes(!showNotes);
    };

    React.useEffect(() => {
        if (typeof localOrderQuantity !== "undefined") {
            validateQuantity();
        }
    }, [localOrderQuantity]);

    const validateQuantity = () => {
        if (localOrderQuantity === "0") {
            return setQuantityError("")
        }
        const errorMessage = validateQuantityField(localOrderQuantity);
        setQuantityError(errorMessage);
        return !errorMessage;
    };

    const toggleNotesHeading = showNotes ? translate("Hide Line Notes") : translate("Show Line Notes");
    const exceedsQtyRemainingError = translate("Order quantity exceeds quantity remaining");
    const qtyRemaining = jobQuoteLine.qtyOrdered! - (jobQuoteLine.qtySold || 0);

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`jobQuoteLine_${jobQuoteLine.productId}_${jobQuoteLine.unitOfMeasure}`}
        >
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={jobQuoteLine} extendedStyles={styles.productImage} />
            </GridItem>
            <GridItem {...styles.quoteLineInfoGridItem}>
                <GridContainer {...styles.quoteLineInfoContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <GridContainer {...styles.infoContainer}>
                            <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                {jobQuoteLine.brand && (
                                    <ProductBrand brand={jobQuoteLine.brand} extendedStyles={styles.productBrand} />
                                )}
                                <ProductDescription product={jobQuoteLine} extendedStyles={styles.productDescriptionStyles} />
                                <Typography>{jobQuoteLine.altText}</Typography>
                                <ProductPartNumbers
                                    productNumber={jobQuoteLine.erpNumber}
                                    customerProductNumber={jobQuoteLine.customerName}
                                    manufacturerItem={jobQuoteLine.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.unitPriceAndQuantitiesGridItem}>
                        <GridContainer {...styles.unitPriceAndQuantitiesContainer}>
                            <GridItem {...styles.unitPriceGridItem}>
                                <Typography {...styles.PriceLabel}>
                                    {translate("Unit Price")}
                                    </Typography>
                                <ProductPrice
                                    product={jobQuoteLine}
                                    currencySymbol={currencySymbol}
                                    showLabel={false}
                                    extendedStyles={styles.productPrice}
                                />
                            </GridItem>
                            <GridItem {...styles.jobQuantityGridItem} data-test-selector="quantityOrdered">
                                <SmallHeadingAndText
                                    heading={translate("Job QTY Orders")}
                                    text={jobQuoteLine.qtyOrdered!}
                                    extendedStyles={styles.jobQuantityHeadingAndText}
                                />
                            </GridItem>
                            <GridItem {...styles.purchasedQuantityGridItem} data-test-selector="orderedPrice">
                                <SmallHeadingAndText
                                    heading={translate("Purchased QTY")}
                                    text={jobQuoteLine.qtySold}
                                    extendedStyles={styles.purchasedQuantityHeadingAndText}
                                />
                            </GridItem>
                            <GridItem {...styles.quantityRemainingGridItem}>
                                <SmallHeadingAndText
                                    heading={translate("QTY Remaining")}
                                    text={qtyRemaining}
                                    extendedStyles={styles.quantityRemainingHeadingAndText}
                                />
                            </GridItem>
                            <GridItem {...styles.qtyOrderedGridItem}>
                                {isEditable && (
                                    <TextField
                                        {...styles.qtyOrderedTextField}
                                        type="number"
                                        label={translate("Order QTY")}
                                        error={qtyOrdered > qtyRemaining ? exceedsQtyRemainingError : quantityError}
                                        value={localOrderQuantity}
                                        disabled={qtyRemaining <= 0}
                                        onChange={qtyOrderedChangeHandler}
                                        onKeyDown={qtyOrderedKeyDownHandler}
                                        onBlur={qtyOrderedBlurHandler}
                                        data-test-selector="jobQuoteDetails_orderQuantity"
                                    />
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {showLineNotes && !jobQuoteLine.isPromotionItem && (
                        <GridItem {...styles.notesGridItem}>
                            {jobQuoteLine.notes && (
                                <Link {...styles.toggleNotesLink} onClick={toggleNotesClickHandler}>
                                    {toggleNotesHeading}
                                </Link>
                            )}
                            {showNotes && <Typography {...styles.notesText}>{jobQuoteLine.notes}</Typography>}
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(RfqJobQuoteDetailsProductCard));
